import {
  describeErrorName,
  recordSerializerFallback,
} from '@/components/tiptap/shared/serializer-fallback';
import {
  isEmphasisWhitespaceExpulsionKilled,
  isScribeBlockEditorCodeblockEnabled,
} from '@/features/flags';
import { isTableRoundTripFixKilled } from '@/features/flags/table-roundtrip-flag';
import { collapseSameTypeListItemGaps } from '@/helpers/listSpacing';
import { stripSectionWrappers } from '@/helpers/sectionParser';
import { logger } from '@/utils/logger/logger';
import { stripUnderlineHtmlPairs } from '@heidi/editor/core';
import { sanitizeError } from '@heidi/runtime/logger';
import TurndownService from 'turndown';

const placeholder = 'ABAEMPTYLINEABA';
const CODE_BLOCK_SENTINEL = 'ABACODEBLOCKABA';

/**
 * Underline tag shapes, consumed by `stripVisualEmphasis`
 * (`copyHelpers/plainTextClipboard.ts`) — the plain-text sink. The copy
 * markdown flavor deliberately keeps `<u>...</u>` as raw HTML: markdown
 * renderers (e.g. internal admin) round-trip it, and stripping it broke the
 * template → Notion code-block → internal admin workflow. Only the plain-text
 * reduction strips underline.
 *
 * The opener's tail requires leading whitespace so longer tag names survive —
 * `<u[^>]*>` eats `<ul>` and strands the `<li>`. Inside the tail, quoted runs
 * alternate with unquoted non-quote characters so an attribute value may carry
 * `>` (`<u title="a>b">`); the alternatives are disjoint, so a malformed tag
 * fails in linear time. The disjointness cuts the other way too: a tag with
 * an *unbalanced* quote never matches at all, where the old `[^>]*` tail
 * "matched" by mis-terminating at the first `>` — a deliberate trade.
 * Exported as sources, not regexes, because the paired form composes them.
 *
 * The pair must never treat a self-closing underline as an opener — a
 * bodyless `<u/>` would capture until the next unrelated `</u>` and strand
 * that pair's real opener in `text/plain`. The pair tail therefore rejects
 * `/` outside quotes, barring every self-closing form (`<u/>`, `<u />`,
 * `<u class="x"/>`) while a `/` inside a quoted value still passes.
 */
export const UNDERLINE_PAIR_OPEN_TAG_SOURCE =
  '<u(?:\\s(?:"[^"]*"|\'[^\']*\'|[^>"\'/])*)?>';
export const UNDERLINE_CLOSE_TAG_SOURCE = '</u\\s*>';

/**
 * Any HTML tag with its attributes, tolerant of newlines and of `>` inside a
 * quoted value. Used to normalize tag-internal whitespace outside quoted
 * values before the global newline strip. Linear per match *attempt*; a
 * pathological document that is thousands of `<letter` starts with no later
 * `>` scans quadratically, since every start walks to end-of-input before
 * failing. Real serializer output always carries closers, so the global scan
 * stays linear in practice.
 */
const HTML_TAG_WITH_ATTRIBUTES =
  /<\/?[a-zA-Z][a-zA-Z0-9-]*(?:\s(?:"[^"]*"|'[^']*'|[^>"'])*)?\/?>/g;

/**
 * Flatten table-cell markup produced by `serializeTableCell` for plain-text
 * destinations (clipboard text/plain, GDT payloads): within pipe-table rows,
 * `<br>` separators become spaces and escaped pipes return to literal `|`.
 * Other lines are left untouched so user-typed text is never rewritten.
 */
export const flattenTableCellMarkupForPlainText = (markdown: string): string =>
  markdown
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed.startsWith('|') || !trimmed.endsWith('|')) return line;
      return line.replace(/<br\s*\/?>/gi, ' ').replace(/\\\|/g, '|');
    })
    .join('\n');

/**
 * Blank cell segment: empty, whitespace, an `&nbsp;`, or the lists-mode
 * blank-line placeholder (substituted before the table pass runs — left
 * unhandled it would bake into the row and later decode to a mid-row newline).
 */
const isBlankCellSegment = (segment: string): boolean =>
  segment
    .split(placeholder)
    .join('')
    .replace(/&nbsp;/gi, '')
    .trim().length === 0;

/**
 * Serialize one table cell's inner HTML into a GFM pipe-cell string. Hard
 * breaks already arrive as `</p><p>` boundaries (see the br→paragraph pass in
 * `simpleHtmlToMarkdown`), so paragraphs join with `<br>` — GFM's escape hatch
 * for multi-line cells — and a literal `|` is escaped so it can't mis-split
 * the row on reload. Blank paragraphs (empty or `&nbsp;`-only) are dropped at
 * the cell edges so they don't leave dangling `<br>`s; interior blanks are
 * kept as blank lines (`<br><br>`).
 */
const serializeTableCell = (cellHtml: string, shieldListTags: boolean): string => {
  // Break-glass: byte-identical to the pre-fix flattening (no <br>, no escape).
  if (isTableRoundTripFixKilled()) {
    return cellHtml.replace(/<\/?p[^>]*>/g, '').trim();
  }
  const segments = cellHtml
    .split(/<\/p>\s*<p[^>]*>/i)
    .map((part) => part.replace(/<\/?p[^>]*>/g, '').trim());
  let start = 0;
  let end = segments.length;
  while (start < end && isBlankCellSegment(segments[start] ?? '')) start++;
  while (end > start && isBlankCellSegment(segments[end - 1] ?? '')) end--;
  const serialized = segments
    .slice(start, end)
    .map((part) => (isBlankCellSegment(part) ? '' : part))
    .join('<br>')
    .replace(/\|/g, '\\|');

  // Shield cell lists only when the later lists-mode pass could rewrite them.
  return shieldListTags
    ? serialized.replace(/<(\/?(?:ol|ul|li)(?:\s[^<>]*)?)>/g, '&lt;$1&gt;')
    : serialized;
};

/**
 * Strip `<pre><code>` blocks out of `html` before the rest of the conversion
 * pipeline runs. Critical because `simpleHtmlToMarkdown` strips all newlines
 * early — which would collapse a multi-line code block into a single line.
 * Paired with `restoreFencedCodeBlocks`.
 */
function extractHtmlCodeBlocks(html: string): { html: string; blocks: string[] } {
  if (!isScribeBlockEditorCodeblockEnabled()) {
    return { html, blocks: [] };
  }
  const blocks: string[] = [];
  const stripped = html.replace(
    /<pre[^>]*>\s*<code[^>]*>([\s\S]*?)<\/code>\s*<\/pre>/gi,
    (_full, inner: string) => {
      const decoded = inner
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&apos;/g, "'")
        .replace(/&nbsp;/g, ' ')
        // &amp; LAST so &amp;lt; -> &lt;, not <
        .replace(/&amp;/g, '&');
      const idx = blocks.length;
      blocks.push('```\n' + decoded + '\n```');
      return `<p>${CODE_BLOCK_SENTINEL}${idx}${CODE_BLOCK_SENTINEL}</p>`;
    }
  );
  return { html: stripped, blocks };
}

function restoreFencedCodeBlocks(text: string, blocks: string[]): string {
  if (blocks.length === 0) return text;
  return text.replace(
    new RegExp(
      `(?:<p>)?${CODE_BLOCK_SENTINEL}(\\d+)${CODE_BLOCK_SENTINEL}(?:</p>)?`,
      'g'
    ),
    (_, idx: string) => blocks[Number(idx)] ?? ''
  );
}

export const removeAnnotationFromMarkdown = (markdownContent: string) => {
  // Bold: strip **text** then __text__ (double markers before single)
  let processedContent = markdownContent.replace(/\*\*(.*?)\*\*/g, '$1');
  processedContent = processedContent.replace(/__(.*?)__/g, '$1');

  // Italic: strip *text* then _text_ (after double markers are consumed)
  processedContent = processedContent.replace(/\*(.*?)\*/g, '$1');
  processedContent = processedContent.replace(/_(.*?)_/g, '$1');

  // Replace underline HTML tags with the shared linear pair strip: the copy
  // serializer no longer pre-strips `<u>`, so Word / Google-Docs shapes
  // (`<u class="x">`, `<U>`) reach this plain-copy annotation strip — and the
  // athena push payloads that build on it — and must be removed too. Same
  // pair-only invariant as the plain sink, in linear time.
  processedContent = stripUnderlineHtmlPairs(processedContent);

  return processedContent;
};

const turndownService = new TurndownService({
  bulletListMarker: '-',
  hr: '---',
  headingStyle: 'atx',
});
// turndownService.use(gfm.tables);

// Keep existing rules for paragraphs and lists
turndownService.addRule('paragraph', {
  filter: 'p',
  replacement: (content, node) => {
    const isNested = node.parentNode?.nodeName.toLowerCase() === 'li';
    return isNested ? content : content + '\n';
  },
});

turndownService.addRule('unorderedList', {
  filter: 'ul',
  replacement: (content, node) => {
    const isNested = node.parentNode?.nodeName.toLowerCase() === 'li';
    return isNested ? '\n' + content : '\n' + content + '\n';
  },
});

turndownService.addRule('orderedList', {
  filter: 'ol',
  replacement: (content, node) => {
    const isNested = node.parentNode?.nodeName.toLowerCase() === 'li';
    return isNested ? '\n' + content : '\n' + content + '\n';
  },
});

turndownService.addRule('listItem', {
  filter: 'li',

  replacement: function (content, node, options) {
    if (!node || !(node instanceof HTMLElement)) {
      logger.warn('Invalid node provided to list item replacement');
      return content;
    }

    content = content
      .replace(/^\n+/, '') // remove leading newlines
      .replace(/\n+$/, '\n') // replace trailing newlines with just a single one
      .replace(/\n/gm, '\n    '); // indent
    let prefix = options.bulletListMarker + ' ';
    const parent = node.parentNode;
    if (parent?.nodeName === 'OL') {
      const start = (parent as HTMLElement).getAttribute('start');
      const index = Array.prototype.indexOf.call(parent.children, node);
      prefix = (start ? Number(start) + index : index + 1) + '. ';
    }
    return prefix + content + (node.nextSibling && !/\n$/.test(content) ? '\n' : '');
  },
});

turndownService.addRule('horizontalRule', {
  filter: 'hr',
  replacement: () => '---' + '\n',
});

// removing heading symbols
// request from MK
turndownService.addRule('heading', {
  filter: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
  replacement: (content) => {
    return content + '\n';
  },
});

// Disable HTML entity escaping to preserve raw text
turndownService.escape = (text: string) => text;

export const htmlToMarkdown = ({
  html,
  useSimpleMarkdownToHtml = false,
}: {
  html: string;
  useSimpleMarkdownToHtml?: boolean;
}) => {
  if (useSimpleMarkdownToHtml) {
    return simpleHtmlToMarkdown({ html });
  }
  if (typeof html !== 'string') {
    throw new TypeError('Input must be a string');
  }
  try {
    const htmlWithPlaceholder = html.replace(/<p><\/p>/g, `<p>${placeholder}</p>`);
    const markdown = turndownService.turndown(htmlWithPlaceholder);
    return markdown.replace(new RegExp(`${placeholder}`, 'g'), '');
  } catch (error) {
    // Counter first: it must increment even if the logger itself throws.
    recordSerializerFallback();
    logger.error('[MD] serializer fell back to input', {
      serializer: 'htmlToMarkdown',
      error: sanitizeError(error),
      error_name: describeErrorName(error),
      input_length: html?.length ?? 0,
    });
    return html; // Fallback to original content
  }
};

interface ConversionOptions {
  bold?: boolean;
  italic?: boolean;
  paragraphs?: boolean;
  horizontalRule?: boolean;
  lists?: boolean;
  /**
   * Section persistence mode. Section editors thread a single opts object
   * through both `simpleMarkdownToHtml` (which gates forward `# `→section
   * wrapping on it) and `simpleHtmlToMarkdown`. Section-wrapper *stripping* is
   * content-driven (unconditional below); this flag gates what the reverse
   * converter *emits*: a stable structural marker for an empty section heading,
   * and a section-close marker between a section and a following top-level
   * block. Off for the optionless copy/export/EHR paths so neither marker ever
   * leaks into user-facing output.
   */
  wrapSections?: boolean;
}

// Emphasis delimiters must hug non-whitespace (CommonMark flanking rules):
// `_Examination _` never re-parses as italic, so a styled run's enclosing
// whitespace is expelled outside the delimiters. A whitespace-only run keeps
// no delimiters at all — `__` / `****` is not valid emphasis either.
// `&nbsp;` counts as whitespace here: it decodes to a plain space only in the
// later entity pass, which would re-create the flanking violation.
const EMPHASIS_EDGE_WHITESPACE = /^((?:\s|&nbsp;)*)([\s\S]*?)((?:\s|&nbsp;)*)$/;
const wrapEmphasis =
  (delimiter: string) =>
  (_match: string, inner: string): string => {
    const [, leading = '', core = '', trailing = ''] =
      EMPHASIS_EDGE_WHITESPACE.exec(inner) ?? [];
    if (core === '') return inner;
    return `${leading}${delimiter}${core}${delimiter}${trailing}`;
  };

// Pre-expulsion behavior — whitespace stays inside the delimiters. Break-glass
// fallback behind the `op-scribe-disable-emphasis-whitespace-expulsion`
// killswitch.
const legacyWrapEmphasis =
  (delimiter: string) =>
  (_match: string, inner: string): string =>
    `${delimiter}${inner}${delimiter}`;

/**
 * Public entry for the vMDE HTML→markdown save/copy serializer. Guards the
 * (previously unguarded) conversion pipeline with a named failure log and a
 * safe fallback to the original HTML, mirroring `htmlToMarkdown` above so a
 * throw is instrumented in Datadog instead of propagating uninstrumented.
 */
export function simpleHtmlToMarkdown(args: {
  html: string;
  options?: ConversionOptions;
  isCopy?: boolean;
}): string {
  try {
    return runSimpleHtmlToMarkdown(args);
  } catch (error) {
    recordSerializerFallback();
    logger.error('[MD] serializer fell back to input', {
      serializer: 'simpleHtmlToMarkdown',
      error: sanitizeError(error),
      error_name: describeErrorName(error),
      input_length: args.html?.length ?? 0,
    });
    return args.html; // Fallback to original content
  }
}

/**
 * Converts HTML text to Markdown with simple rules.
 * You can disable specific conversion rules by passing an options object.
 * This function inverses the transformation applied by simpleMarkdownToHtml.
 */
function runSimpleHtmlToMarkdown({
  html,
  options = {},
  isCopy = false,
}: {
  html: string;
  options?: ConversionOptions;
  isCopy?: boolean;
}): string {
  // Set defaults: if an option is NOT explicitly false, it'll be enabled.
  const opts = {
    bold: options.bold !== false,
    italic: options.italic !== false,
    paragraphs: options.paragraphs !== false,
    horizontalRule: options.horizontalRule !== false,
  };

  // Section-wrapper stripping is content-driven, not flag-gated: stripSectionWrappers
  // is a no-op when the HTML has no `<section>` wrappers, so running it unconditionally
  // normalizes section-node markup back to `# ` headings for EVERY caller — including the
  // optionless copy/export paths (copyPlainTextV2, shortcut copy, formatted copy,
  // copy-settings export) that serialize a section-enabled editor's getHTML(). Markdown
  // has no `<section>` concept, so this is lossless everywhere; the forward `wrapSections`
  // gate (markdownToHtml) stays as-is.
  // Marker emission is the persistence conversion only (wrapSections — the gate
  // the section editors thread), never on copy: both markers are storage-only
  // structural tokens and must not surface in the clipboard, so a caller that
  // ever combines wrapSections with isCopy still gets clean output.
  const emitPersistenceMarkers = options.wrapSections === true && !isCopy;
  html = stripSectionWrappers(html, {
    emitEmptySectionHeadingMarker: emitPersistenceMarkers,
    emitSectionEndMarkers: emitPersistenceMarkers,
  });

  // Stash <pre><code> blocks before the \n strip below — their content is
  // opaque and must preserve newlines for the markdown fence output.
  const codeBlockExtraction = extractHtmlCodeBlocks(html);
  html = codeBlockExtraction.html;

  // A newline inside a tag would glue the name to its attributes
  // (`<u\nclass="x">` → `<uclass="x">`), leaving a shape no tag pattern below
  // can match while its closer still goes. Collapse tag-internal whitespace
  // first, passing quoted attribute values through untouched — the save path
  // persists them verbatim — and drop the run before the closing bracket
  // entirely: the newline strip used to delete it, and the glue-form matchers
  // below (`<li><p>`, `</p>`, `<hr>`) depend on that. The rest of the document
  // keeps the unconditional strip.
  html = html.replace(HTML_TAG_WITH_ATTRIBUTES, (tag) =>
    tag
      .replace(
        /("[^"]*"|'[^']*')|\s+/g,
        (_run, quoted: string | undefined) => quoted ?? ' '
      )
      .replace(/ (\/?>)$/, '$1')
  );
  html = html.replace(/\n/g, '');

  // remove span tags
  html = html.replace(/<\/?span[^>]*>/g, '');

  // remove keypoints tags (SCR-2599: strip embedded keypoints tags that cause weird spacing)
  html = html.replace(/<\/?keypoints[^>]*>/g, '');

  const useLists = options.lists === true;

  // replace br with empty paragraph
  html = html.replace(/<br\s*\/?>/g, '</p><p>');

  if (opts.horizontalRule) {
    html = html.replace(/<hr>/g, '<p>---</p>');
  }

  const BLANK_LINE_MARKER = 'ABAEMPTYLINEABA';
  if (useLists) {
    html = html.replace(/<p><\/p>/g, `<p>${BLANK_LINE_MARKER}</p>`);
  }

  // Killed reproduces the pre-expulsion output byte-for-byte (the pass order
  // below is output-neutral without expulsion).
  const emphasis = isEmphasisWhitespaceExpulsionKilled()
    ? legacyWrapEmphasis
    : wrapEmphasis;

  // Italic before bold: ProseMirror nests <strong> outside <em>, so the em
  // pass can leave expelled whitespace at the strong boundary
  // (`<strong>a <em>b </em></strong>` → `<strong>a _b_ </strong>`); the bold
  // pass then expels it in turn. The reverse nesting (em wrapping strong)
  // isn't ProseMirror output; its bytes shift under this order but its italic
  // delimiters remain a flanking violation either way.
  if (opts.italic) {
    html = html.replace(/<em\b[^>]*>([\s\S]*?)<\/em>/gi, emphasis('_'));
    html = html.replace(/<i\b[^>]*>([\s\S]*?)<\/i>/gi, emphasis('_'));
  }

  if (opts.bold) {
    html = html.replace(/<strong\b[^>]*>([\s\S]*?)<\/strong>/gi, emphasis('**'));
    html = html.replace(/<b\b[^>]*>([\s\S]*?)<\/b>/gi, emphasis('**'));
  }

  // Convert HTML tables to markdown table syntax before paragraph processing
  html = html.replace(
    /<table[^>]*>([\s\S]*?)<\/table>/gi,
    (_match: string, tableContent: string) => {
      const rows: string[][] = [];
      let hasHeader = false;

      // Extract header cells from <thead> (if present)
      const theadMatch = tableContent.match(/<thead[^>]*>([\s\S]*?)<\/thead>/i);
      if (theadMatch) {
        const headerCells: string[] = [];
        const thRegex = /<th[^>]*>([\s\S]*?)<\/th>/gi;
        let thMatch;
        while ((thMatch = thRegex.exec(theadMatch[1] ?? '')) !== null) {
          const cellContent = serializeTableCell(thMatch[1] ?? '', useLists);
          headerCells.push(cellContent);
        }
        if (headerCells.length > 0) {
          rows.push(headerCells);
          rows.push(headerCells.map(() => '---'));
          hasHeader = true;
        }
      }

      // Extract rows from <tbody> or directly from <tr> elements
      const tbodyMatch = tableContent.match(/<tbody[^>]*>([\s\S]*?)<\/tbody>/i);
      const bodyHtml = tbodyMatch ? (tbodyMatch[1] ?? '') : tableContent;
      const trRegex = /<tr[^>]*>([\s\S]*?)<\/tr>/gi;
      let trMatch;
      while ((trMatch = trRegex.exec(bodyHtml)) !== null) {
        // Skip rows already captured in thead
        if (
          theadMatch &&
          tableContent.indexOf(trMatch[0]) < (tableContent.indexOf('</thead>') ?? 0)
        ) {
          continue;
        }
        const rowHtml = trMatch[1] ?? '';

        // Check if this row has <th> cells (Tiptap puts header <th> inside <tbody>, not <thead>)
        const isHeaderRow = !hasHeader && /<th[\s>]/i.test(rowHtml);

        if (isHeaderRow) {
          const headerCells: string[] = [];
          const thRegex = /<th[^>]*>([\s\S]*?)<\/th>/gi;
          let thMatch;
          while ((thMatch = thRegex.exec(rowHtml)) !== null) {
            const cellContent = serializeTableCell(thMatch[1] ?? '', useLists);
            headerCells.push(cellContent);
          }
          if (headerCells.length > 0) {
            rows.push(headerCells);
            rows.push(headerCells.map(() => '---'));
            hasHeader = true;
          }
        } else {
          // Extract <td> cells (also match <th> in body rows after header is set)
          const cells: string[] = [];
          const cellRegex = /<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/gi;
          let cellMatch;
          while ((cellMatch = cellRegex.exec(rowHtml)) !== null) {
            const cellContent = serializeTableCell(cellMatch[1] ?? '', useLists);
            cells.push(cellContent);
          }
          if (cells.length > 0) {
            rows.push(cells);
          }
        }
      }

      if (rows.length === 0) return '';

      // Convert rows to markdown table syntax
      const markdownRows = rows.map((row) => `| ${row.join(' | ')} |`);
      return `<p>${markdownRows.join('</p><p>')}</p>`;
    }
  );

  if (useLists) {
    // A `# ` heading (a section's `<h1>` after stripSectionWrappers) owns a
    // 2-newline standard separator from preceding content — one more newline than
    // the generic content gap. Run this after table serialization so paragraph
    // boundaries inside cells cannot be mistaken for top-level section
    // boundaries. A top-level table is now a markdown-row paragraph, which still
    // matches the generic </p><p># form below.
    html = html.replace(/(<\/(?:p|ul|ol)>)<p># /g, `$1<p>${BLANK_LINE_MARKER}</p><p># `);

    html = html.replace(/<li><p>/g, '<li>');
    html = html.replace(/<\/p><\/li>/g, '</li>');

    // Trailing newline after each list keeps a following paragraph on its own
    // line — without it, text immediately after </ul>/</ol> glues onto the last
    // item (e.g. "- last itemNext heading") and gets absorbed into the list.
    html = html.replace(/<ul[^>]*>(.*?)<\/ul>/g, (_match, inner: string) => {
      const items = [...inner.matchAll(/<li>(.*?)<\/li>/g)];
      return items.map((m) => `\n- ${m[1]}`).join('') + '\n';
    });

    html = html.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/g, (_match, inner: string) => {
      let counter = 0;
      const items = [...inner.matchAll(/<li>([\s\S]*?)<\/li>/g)];
      return (
        items
          .map((m) => {
            counter++;
            return `\n${counter}. ${m[1]}`;
          })
          .join('') + '\n'
      );
    });

    html = html.replace(/<\/?(?:ol|ul|li)[^>]*>/g, '');
  }

  // Process paragraphs: Remove wrapping <p> tags and convert them to newline separated text.
  if (opts.paragraphs) {
    // Replace opening <p> tags with empty string
    html = html.replace(/<p[^>]*>/g, '');
    // Replace closing </p> tags with newline, except the last occurrence, which is replaced with an empty string
    const closingTags = html.match(/<\/p>/g);
    if (closingTags) {
      const count = closingTags.length;
      let current = 0;
      html = html.replace(/<\/p>/g, () => {
        current++;
        return current === count ? '' : '\n';
      });
    }
  }

  if (useLists) {
    // A paragraph's </p> becomes "\n" and each list emits its own leading "\n",
    // so a real paragraph immediately followed by a list produces a doubled
    // "\n\n" before the first item. Collapse it back to a single newline. Runs
    // before the empty-line marker is expanded, so an intentional blank line
    // before a list (still a marker token here, not bare newlines) is untouched.
    html = html.replace(/\n{2,}(?=(?:- |\d+\. ))/g, '\n');
  }

  // Decode text entities after real HTML tags have been converted. This keeps
  // user-typed escaped tags like &lt;b&gt; literal instead of converting them.
  html = html.replace(/&gt;/g, '>');
  html = html.replace(/&ge;/g, '>=');
  html = html.replace(/&lt;/g, '<');
  html = html.replace(/&le;/g, '<=');
  html = html.replace(/&quot;/g, '"');
  html = html.replace(/&#39;/g, "'");
  html = html.replace(/&apos;/g, "'");

  html = html.replace(/&nbsp;/g, ' ');

  // &amp; (processed last to avoid double unescaping)
  html = html.replace(/&amp;/g, '&');

  if (useLists) {
    // Each empty paragraph's own </p> already emitted one newline above, so the
    // marker itself expands to nothing: k empty paragraphs → (k+1) newlines, the
    // exact inverse of the parser's "run of N newlines → (N-1) empty paragraphs".
    // Both document edges are one newline short of that and each has to supply
    // its own: at the start there is no preceding paragraph, and at the end the
    // paragraph pass above deliberately drops the final </p> so ordinary notes
    // don't gain a trailing newline. Without the trailing case, blank lines
    // typed at the very end of a note serialise one newline short and read back
    // as none.
    const lastMarkerOffset = html.lastIndexOf(BLANK_LINE_MARKER);
    const endsOnBlankLines =
      lastMarkerOffset !== -1 &&
      /^\n*$/.test(html.slice(lastMarkerOffset + BLANK_LINE_MARKER.length));
    html = html.replace(
      new RegExp(BLANK_LINE_MARKER, 'g'),
      (_marker: string, offset: number) =>
        offset === 0 || (endsOnBlankLines && offset === lastMarkerOffset) ? '\n' : ''
    );
    // Keep saved markdown tight: collapse a stray blank between consecutive
    // same-type list fragments so it never reaches storage and compounds.
    html = collapseSameTypeListItemGaps(html);
  }

  if (isCopy) {
    html = html.replace(/→/g, '->');
    html = html.replace(/←/g, '<-');

    // Underline is deliberately NOT stripped here: the copy markdown is a
    // round-trip format consumed by markdown renderers (internal admin), and
    // `<u>...</u>` is legal raw HTML in markdown. Only the plain-text sink
    // (`stripMarkdownForPlainText` / `removeAnnotationFromMarkdown`) strips it.

    // Clipboard text/plain must not carry cell-internal <br> / \| markup.
    html = flattenTableCellMarkupForPlainText(html);
  }

  html = restoreFencedCodeBlocks(html, codeBlockExtraction.blocks);

  return html;
}
